import { useNavigate, useParams } from 'react-router-dom';
import { FileSpreadsheet, Pencil, Trash2 } from 'lucide-react';
import { Button, ErrorState, Loading, PageHeader, Panel, StatusBadge, Tabs, useConfirm, useToast } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { hasRole } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { formatNumber } from '@/lib/format';
import { toWareki, weekdayJa } from '@/lib/wareki';
import { CATCH_LABELS, useDeleteVoyage, useVoyage, type VoyageDetail } from './api';
import { GearReadTable, SightingReadTable } from './components/SightingReadTables';
import '@/features/advance-notices/advance-notices.css';
import './voyage-reports.css';

/** S-42 速報 詳細 */
export function VoyageDetailPage() {
  const { id } = useParams();
  const { data, isPending, error, refetch } = useVoyage(Number(id));
  if (isPending) return <Loading block />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <Detail r={data} />;
}

function Detail({ r }: { r: VoyageDetail }) {
  const user = useCurrentUser();
  const canEdit = hasRole(user, ['基金', '漁協']);
  const { yearPath } = useFiscalYear();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const remove = useDeleteVoyage();
  const onPrint = async () => {
    try {
      toast.success('調査・監視報告書を出力しました', await downloadFile(`/voyage-reports/${r.id}/report.xlsx`));
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    }
  };
  const onDelete = async () => {
    if (!(await confirm({ title: '速報を削除しますか？', message: '調査内容・燃油代と、この速報のみで登録した視認・漁具情報も削除されます。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await remove.mutateAsync(r.id);
      toast.success('速報を削除しました');
      navigate(yearPath('voyage-reports'));
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };
  const sum = r.surveyDays.reduce((a, d) => ({ wage: a.wage + d.wageTotal, charter: a.charter + d.charterTotal }), { wage: 0, charter: 0 });
  const e = r.expense;
  const kv: [string, string][] = [
    ['事業実施者', r.businessOperatorName],
    ['漁船登録番号', r.registrationNo],
    ['船舶トン数', `${formatNumber(r.tonnage, 2)}トン`],
    ['調査期間', `${toWareki(r.periodFrom)}（${weekdayJa(r.periodFrom)}）～ ${toWareki(r.periodTo)}（${weekdayJa(r.periodTo)}）`],
    ['船長', r.captainName],
    ['乗船調査員②・③', [r.surveyor2Name, r.surveyor3Name].filter(Boolean).join('・') || '—'],
    ['乗船人員数・担い手', `${r.crewCount}人・担い手 ${r.bearerCount}人`],
    ['実作業日数', `${formatNumber(r.actualWorkDays, 2)}日`],
    ['インボイス対象調査', r.isInvoiceTargetSurvey ? '対象' : '対象外'],
    ['漁獲調査等', r.catchSurveyStatus ? `${r.catchSurveyStatus}：${CATCH_LABELS[r.catchSurveyStatus]}` : '未入力'],
    ['報告書提出日', r.reportSubmittedOn ? toWareki(r.reportSubmittedOn) : '—'],
    ['報告者氏名', r.reporterName ?? '—'],
  ];
  return (
    <>
      <PageHeader
        eyebrow="日々の報告 ／ 速報"
        title={`速報 ${r.vesselName}`}
        meta={r.catchSurveyStatus ? <StatusBadge tone="info">漁獲調査等 {r.catchSurveyStatus}</StatusBadge> : <StatusBadge tone="warning">漁獲調査等 未入力</StatusBadge>}
        description={`${r.periodFrom.replaceAll('-', '/')} ～ ${r.periodTo.replaceAll('-', '/')}`}
        actions={
          <>
            <Button icon={<FileSpreadsheet size={16} />} onClick={() => void onPrint()}>
              帳票出力
            </Button>
            {canEdit && (
              <>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()}>
                  削除
                </Button>
                <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`voyage-reports/${r.id}/edit`))} disabled={!r.noticeVesselId}>
                  編集
                </Button>
              </>
            )}
          </>
        }
      />
      <Panel title="基本情報">
        <dl className="bs-kv">
          {kv.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      <div className="bs-g2-gap" />
      <Tabs
        items={[
          {
            key: 'survey',
            label: '調査内容',
            badge: r.surveyDays.length,
            content: (
              <Panel flush>
                <table className="bs-survey-table">
                  <thead>
                    <tr>
                      <th>調査日</th>
                      <th className="is-num">時間</th>
                      <th>調査水域</th>
                      <th>判定</th>
                      <th className="is-num">賃金（税抜）</th>
                      <th className="is-num">賃金 消費税</th>
                      <th className="is-num">用船料（税抜）</th>
                      <th className="is-num">用船料 消費税</th>
                      <th>助成金支払予定日／支払日</th>
                      <th>感想</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.surveyDays.map((d) => (
                      <tr key={d.id}>
                        <td className="is-num">
                          {d.surveyDate.slice(5).replace('-', '/')}（{weekdayJa(d.surveyDate)}）
                        </td>
                        <td className="is-num">{d.surveyHours}</td>
                        <td className="bs-num">
                          {d.zoneNumbers.map((z, k) => (
                            <span key={k} style={{ marginRight: 6 }}>
                              {z}
                              {d.zoneClasses[k] ? <span className={`bs-cls is-${d.zoneClasses[k]}`}>{d.zoneClasses[k]}</span> : null}
                            </span>
                          ))}
                        </td>
                        <td>{d.surveyClass && <span className={`bs-cls is-${d.surveyClass}`}>{d.surveyClass}</span>}</td>
                        <td className="is-num">{formatNumber(d.wageExclTax)}</td>
                        <td className="is-num">{formatNumber(d.wageTax)}</td>
                        <td className="is-num">{formatNumber(d.charterExclTax)}</td>
                        <td className="is-num">{formatNumber(d.charterTax)}</td>
                        <td className="is-num">
                          {d.subsidyScheduledOn ?? '—'} ／ {d.subsidyPaidOn ?? '—'}
                        </td>
                        <td>{d.impression}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bs-survey-total">
                      <td colSpan={4}>合計（税込：賃金 {formatNumber(sum.wage)}円 ／ 用船料 {formatNumber(sum.charter)}円）</td>
                      <td className="is-num">{formatNumber(r.surveyDays.reduce((a, d) => a + d.wageExclTax, 0))}</td>
                      <td className="is-num">{formatNumber(r.surveyDays.reduce((a, d) => a + d.wageTax, 0))}</td>
                      <td className="is-num">{formatNumber(r.surveyDays.reduce((a, d) => a + d.charterExclTax, 0))}</td>
                      <td className="is-num">{formatNumber(r.surveyDays.reduce((a, d) => a + d.charterTax, 0))}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </Panel>
            ),
          },
          { key: 'sightings', label: '視認状況報告', badge: r.sightings.length, content: <Panel>{<SightingReadTable rows={r.sightings} />}</Panel> },
          { key: 'gears', label: '漁場調査等', badge: r.gearFindings.length, content: <Panel>{<GearReadTable rows={r.gearFindings} />}</Panel> },
          {
            key: 'expense',
            label: '燃油代・潤滑油・現像代',
            content: (
              <Panel>
                {e ? (
                  <dl className="bs-kv">
                    <div>
                      <dt>燃油 給油日・種類</dt>
                      <dd>
                        {e.fuelDate ?? '—'}　{e.fuelType ?? ''}
                        {e.fuelDaysAfterReturn != null && <span className="bs-cell-sub">帰港日から {e.fuelDaysAfterReturn}日</span>}
                      </dd>
                    </div>
                    <div>
                      <dt>燃油 数量・単価</dt>
                      <dd className="bs-num">
                        {formatNumber(e.fuelQuantity, 2)}ℓ × {formatNumber(e.fuelUnitPrice, 2)}円
                      </dd>
                    </div>
                    <div>
                      <dt>燃油 税抜・消費税・引取税</dt>
                      <dd className="bs-num">
                        {formatNumber(e.fuelAmountExclTax)}・{formatNumber(e.fuelTax)}・{formatNumber(e.fuelLightOilTax)}
                      </dd>
                    </div>
                    <div>
                      <dt>燃油 合計（税込）</dt>
                      <dd className="bs-num">{formatNumber(e.fuelTotal)}円</dd>
                    </div>
                    <div>
                      <dt>潤滑油（係数・単価）</dt>
                      <dd className="bs-num">
                        {e.lubeCoefficient ?? '—'} ・ {formatNumber(e.lubeUnitPrice, 2)}円
                      </dd>
                    </div>
                    <div>
                      <dt>潤滑油 税抜・合計</dt>
                      <dd className="bs-num">
                        {formatNumber(e.lubeAmountExclTax)}・{formatNumber(e.lubeTotal)}円
                      </dd>
                    </div>
                    <div>
                      <dt>現像代 税抜・消費税</dt>
                      <dd className="bs-num">
                        {formatNumber(e.devAmountExclTax)}・{formatNumber(e.devTax)}
                      </dd>
                    </div>
                    <div>
                      <dt>現像代 合計（税込）</dt>
                      <dd className="bs-num">{formatNumber(e.devTotal)}円</dd>
                    </div>
                    <div className="is-full">
                      <dt>燃油 備考</dt>
                      <dd>{e.fuelNote || '—'}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="bs-cell-sub">燃油代等の登録はありません。</p>
                )}
              </Panel>
            ),
          },
        ]}
      />
    </>
  );
}
